import { useState } from 'react'
import {
  Network,
  Plus,
  Server,
  Globe,
  CheckCircle2,
  RefreshCw,
  Settings,
  AlertTriangle,
  Shield,
  Save,
} from 'lucide-react'
import styles from './HaproxyPage.module.css'

interface Frontend {
  id: string
  name: string
  bindAddress: string
  mode: 'http' | 'tcp'
  ssl: boolean
  backend: string
  connections: number
}

interface Backend {
  id: string
  name: string
  balanceMode: string
  serverCount: number
  healthStatus: 'healthy' | 'degraded' | 'down'
  activeConn: number
  errorRate: string
}

const MOCK_FRONTENDS: Frontend[] = [
  { id: 'fe-001', name: 'http-in',         bindAddress: '0.0.0.0:80',   mode: 'http', ssl: false, backend: 'ssp-api-backend',      connections: 2841 },
  { id: 'fe-002', name: 'https-in',        bindAddress: '0.0.0.0:443',  mode: 'http', ssl: true,  backend: 'ssp-api-backend',      connections: 14203 },
  { id: 'fe-003', name: 'pay-edi-fe',      bindAddress: '0.0.0.0:8443', mode: 'http', ssl: true,  backend: 'pay-edi-backend',      connections: 4512 },
  { id: 'fe-004', name: 'appcentral-fe',   bindAddress: '0.0.0.0:9443', mode: 'http', ssl: true,  backend: 'appcentral-backend',   connections: 8934 },
  { id: 'fe-005', name: 'genai-fe',        bindAddress: '0.0.0.0:9080', mode: 'http', ssl: false, backend: 'genai-backend',        connections: 1203 },
  { id: 'fe-006', name: 'monitoring-fe',   bindAddress: '0.0.0.0:3000', mode: 'http', ssl: false, backend: 'grafana-backend',      connections: 87 },
  { id: 'fe-007', name: 'metrics-tcp',     bindAddress: '0.0.0.0:9090', mode: 'tcp',  ssl: false, backend: 'prometheus-backend',   connections: 23 },
  { id: 'fe-008', name: 'agent-fe',        bindAddress: '0.0.0.0:8080', mode: 'http', ssl: false, backend: 'agent-central-backend',connections: 512 },
]

const MOCK_BACKENDS: Backend[] = [
  { id: 'be-001', name: 'ssp-api-backend',       balanceMode: 'roundrobin', serverCount: 3, healthStatus: 'healthy',  activeConn: 14203, errorRate: '0.01%' },
  { id: 'be-002', name: 'pay-edi-backend',        balanceMode: 'leastconn',  serverCount: 3, healthStatus: 'healthy',  activeConn: 4512,  errorRate: '0.00%' },
  { id: 'be-003', name: 'appcentral-backend',     balanceMode: 'roundrobin', serverCount: 4, healthStatus: 'healthy',  activeConn: 8934,  errorRate: '0.02%' },
  { id: 'be-004', name: 'genai-backend',          balanceMode: 'leastconn',  serverCount: 2, healthStatus: 'healthy',  activeConn: 1203,  errorRate: '0.00%' },
  { id: 'be-005', name: 'grafana-backend',        balanceMode: 'roundrobin', serverCount: 1, healthStatus: 'healthy',  activeConn: 87,    errorRate: '0.00%' },
  { id: 'be-006', name: 'prometheus-backend',     balanceMode: 'source',     serverCount: 1, healthStatus: 'healthy',  activeConn: 23,    errorRate: '0.00%' },
  { id: 'be-007', name: 'agent-central-backend',  balanceMode: 'roundrobin', serverCount: 2, healthStatus: 'degraded', activeConn: 512,   errorRate: '1.23%' },
  { id: 'be-008', name: 'm2m-backend',            balanceMode: 'leastconn',  serverCount: 0, healthStatus: 'down',     activeConn: 0,     errorRate: '100%'  },
]

export function HaproxyPage() {
  const [showApplyConfirm, setShowApplyConfirm] = useState(false)
  const [selectedFe, setSelectedFe] = useState<string | null>(null)

  const totalReqPerSec = 12400
  const uptime = '99.97%'

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}>
            <Network size={20} />
          </div>
          <div>
            <h1 className={styles.pageTitle}>HAProxy Configuration</h1>
            <p className={styles.pageSubtitle}>Manage load balancer frontends, backends, and routing rules</p>
          </div>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.btnSecondary}>
            <Plus size={14} />
            Add Frontend
          </button>
          <button className={styles.btnPrimary} onClick={() => setShowApplyConfirm(true)}>
            <Save size={14} />
            Apply Config
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'var(--dc-infra-bg)', color: 'var(--dc-infra)' }}>
            <Globe size={18} />
          </div>
          <div>
            <div className={styles.statValue}>{MOCK_FRONTENDS.length}</div>
            <div className={styles.statLabel}>Frontends</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'var(--dc-infra-bg)', color: 'var(--dc-infra)' }}>
            <Server size={18} />
          </div>
          <div>
            <div className={styles.statValue}>{MOCK_BACKENDS.length}</div>
            <div className={styles.statLabel}>Backends</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'var(--aptean-success-bg)', color: 'var(--aptean-success)' }}>
            <RefreshCw size={18} />
          </div>
          <div>
            <div className={styles.statValue}>{(totalReqPerSec / 1000).toFixed(1)}k</div>
            <div className={styles.statLabel}>Req/s</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'var(--aptean-success-bg)', color: 'var(--aptean-success)' }}>
            <CheckCircle2 size={18} />
          </div>
          <div>
            <div className={styles.statValue}>{uptime}</div>
            <div className={styles.statLabel}>Uptime</div>
          </div>
        </div>
      </div>

      {/* Two-panel layout */}
      <div className={styles.twoPanel}>
        {/* Frontends */}
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <Globe size={16} />
            <span>Frontends</span>
            <span className={styles.panelCount}>{MOCK_FRONTENDS.length}</span>
          </div>
          <div className={styles.panelBody}>
            {MOCK_FRONTENDS.map((fe) => (
              <div
                key={fe.id}
                className={`${styles.feRow} ${selectedFe === fe.id ? styles.feRowSelected : ''}`}
                onClick={() => setSelectedFe(selectedFe === fe.id ? null : fe.id)}
              >
                <div className={styles.feLeft}>
                  <div className={styles.feStatus}>
                    <span className={styles.statusDotGreen} />
                  </div>
                  <div>
                    <div className={styles.feName}>{fe.name}</div>
                    <div className={styles.feBind}>
                      <code>{fe.bindAddress}</code>
                    </div>
                  </div>
                </div>
                <div className={styles.feRight}>
                  <span className={`${styles.modeBadge} ${fe.mode === 'http' ? styles.modeHttp : styles.modeTcp}`}>
                    {fe.mode.toUpperCase()}
                  </span>
                  {fe.ssl && <Shield size={13} className={styles.sslIcon} />}
                  <span className={styles.feConnections}>{fe.connections.toLocaleString()}</span>
                </div>
                {selectedFe === fe.id && (
                  <div className={styles.feDetail}>
                    <div className={styles.feDetailRow}>
                      <span className={styles.feDetailLabel}>Backend</span>
                      <span className={styles.feDetailValue}>{fe.backend}</span>
                    </div>
                    <div className={styles.feDetailRow}>
                      <span className={styles.feDetailLabel}>SSL</span>
                      <span className={styles.feDetailValue}>{fe.ssl ? 'Enabled' : 'Disabled'}</span>
                    </div>
                    <div className={styles.feDetailRow}>
                      <span className={styles.feDetailLabel}>Active Conns</span>
                      <span className={styles.feDetailValue}>{fe.connections.toLocaleString()}</span>
                    </div>
                    <button className={styles.editBtn}>
                      <Settings size={12} /> Edit
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Backends */}
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <Server size={16} />
            <span>Backends</span>
            <span className={styles.panelCount}>{MOCK_BACKENDS.length}</span>
          </div>
          <div className={styles.panelBody}>
            {MOCK_BACKENDS.map((be) => (
              <div key={be.id} className={styles.beRow}>
                <div className={styles.beTop}>
                  <div className={styles.beLeft}>
                    <span className={`${styles.statusDot} ${
                      be.healthStatus === 'healthy'  ? styles.statusDotGreen :
                      be.healthStatus === 'degraded' ? styles.statusDotAmber :
                      styles.statusDotRed
                    }`} />
                    <div>
                      <div className={styles.beName}>{be.name}</div>
                      <div className={styles.beBalance}>Balance: {be.balanceMode}</div>
                    </div>
                  </div>
                  <div className={styles.beRight}>
                    <span className={`${styles.healthBadge} ${
                      be.healthStatus === 'healthy'  ? styles.healthGreen :
                      be.healthStatus === 'degraded' ? styles.healthAmber :
                      styles.healthRed
                    }`}>
                      {be.healthStatus === 'healthy'  && <CheckCircle2 size={10} />}
                      {be.healthStatus === 'degraded' && <AlertTriangle size={10} />}
                      {be.healthStatus === 'down'     && <AlertTriangle size={10} />}
                      {be.healthStatus}
                    </span>
                  </div>
                </div>
                <div className={styles.beMetaRow}>
                  <span className={styles.beMeta}>
                    <Server size={11} />
                    {be.serverCount} server{be.serverCount !== 1 ? 's' : ''}
                  </span>
                  <span className={styles.beMeta}>{be.activeConn.toLocaleString()} conns</span>
                  <span className={`${styles.beMeta} ${parseFloat(be.errorRate) > 0.5 ? styles.beMetaError : ''}`}>
                    {be.errorRate} err
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Apply Confirm Modal */}
      {showApplyConfirm && (
        <div className={styles.modalOverlay} onClick={() => setShowApplyConfirm(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalIconWrap}>
              <AlertTriangle size={28} />
            </div>
            <h2 className={styles.modalTitle}>Apply HAProxy Configuration?</h2>
            <p className={styles.modalBody}>
              This will reload HAProxy with the current configuration. Active connections will be gracefully maintained.
              Confirm to proceed.
            </p>
            <div className={styles.modalActions}>
              <button className={styles.btnSecondary} onClick={() => setShowApplyConfirm(false)}>Cancel</button>
              <button className={styles.btnPrimary} onClick={() => setShowApplyConfirm(false)}>
                <Save size={14} />
                Apply Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
